import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import apiClient from '../api/client';
import {
  FIT_REPORT_EXERCISE_CONFIG,
  columnsForExercise,
  columnLabel,
} from '../lib/fitReportExerciseConfig';

/**
 * 측정 데이터 입력 카드 (strength / muscular_endurance / cardio).
 * - 1~5행 입력 가능
 * - 각 행은 종목 + 동적 컬럼들 → 백엔드 `/admin/fit-reports/score-preview`로 실시간 점수 계산 (debounced)
 * - 영역 평균 점수 = avg of 행 점수 (5행이면 최저 1건 제외)
 *
 * Props:
 *   category: 'strength' | 'muscular_endurance' | 'cardio'
 *   memberId: string
 *   rows: Array<{ exercise, raw_metrics, score, _key }>
 *   onChange: (newRows) => void
 */
export default function MeasurementCategoryCard({ category, memberId, rows, onChange }) {
  const cat = FIT_REPORT_EXERCISE_CONFIG[category];
  if (!cat) return null;

  const defaultExercise = cat.exercises[0]?.value;

  const handleAddRow = () => {
    if (rows.length >= 5) return;
    const newRow = {
      _key: `${category}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      exercise: defaultExercise,
      raw_metrics: {},
      score: null,
      computing: false,
    };
    onChange([...rows, newRow]);
  };

  const handleRemoveRow = (key) => {
    onChange(rows.filter((r) => r._key !== key));
  };

  const handleUpdateRow = (key, partial) => {
    onChange(rows.map((r) => (r._key === key ? { ...r, ...partial } : r)));
  };

  // 영역 평균
  const validScores = rows.map((r) => r.score).filter((s) => s !== null && s !== undefined && !Number.isNaN(s));
  let areaAvg = null;
  if (validScores.length > 0) {
    const arr = validScores.length >= 5 ? [...validScores].sort((a, b) => a - b).slice(1) : validScores;
    areaAvg = Math.round((arr.reduce((s, v) => s + v, 0) / arr.length) * 10) / 10;
  }

  return (
    <Card data-testid={`measurement-card-${category}`}>
      <CardContent className="pt-5 pb-5 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-base font-medium text-gray-900">{cat.label}</Label>
            <p className="text-xs text-gray-500 mt-0.5">최대 5개 종목까지 입력 가능 · 점수는 자동 계산됩니다</p>
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-500">영역 평균</div>
            <div className="text-2xl font-bold text-orange-500">{areaAvg ?? '-'}</div>
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="text-sm text-gray-400 py-6 text-center border border-dashed border-gray-200 rounded-lg">
            아직 입력된 종목이 없습니다. 아래 버튼으로 추가하세요.
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((row) => (
              <MeasurementRow
                key={row._key}
                row={row}
                category={category}
                memberId={memberId}
                catConfig={cat}
                onUpdate={(p) => handleUpdateRow(row._key, p)}
                onRemove={() => handleRemoveRow(row._key)}
              />
            ))}
          </div>
        )}

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddRow}
          disabled={rows.length >= 5}
          data-testid={`btn-add-row-${category}`}
        >
          <Plus className="w-4 h-4 mr-1" /> 종목 추가 ({rows.length}/5)
        </Button>
      </CardContent>
    </Card>
  );
}

function MeasurementRow({ row, category, memberId, catConfig, onUpdate, onRemove }) {
  const cols = columnsForExercise(category, row.exercise);
  const [computing, setComputing] = useState(false);
  const debounceRef = useRef(null);

  // Debounced score preview
  useEffect(() => {
    if (!memberId || !row.exercise) return;
    // require all displayed columns to have value before requesting
    const hasAllVals = cols.every((c) => {
      const v = row.raw_metrics?.[c];
      return v !== undefined && v !== null && v !== '';
    });
    if (!hasAllVals) {
      if (row.score !== null) onUpdate({ score: null });
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setComputing(true);
      try {
        const numericMetrics = {};
        for (const k of Object.keys(row.raw_metrics || {})) {
          const v = row.raw_metrics[k];
          const n = Number(v);
          numericMetrics[k] = Number.isNaN(n) ? v : n;
        }
        const res = await apiClient.post('/admin/fit-reports/score-preview', {
          member_id: memberId,
          category,
          exercise: row.exercise,
          raw_metrics: numericMetrics,
        });
        onUpdate({ score: res.data?.score ?? null });
      } catch (e) {
        onUpdate({ score: null });
      } finally {
        setComputing(false);
      }
    }, 350);

    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.exercise, JSON.stringify(row.raw_metrics), memberId]);

  const handleMetricChange = (col, value) => {
    onUpdate({ raw_metrics: { ...row.raw_metrics, [col]: value } });
  };

  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50/40" data-testid={`row-${category}-${row._key}`}>
      <div className="flex items-end gap-2 flex-wrap">
        {/* Exercise select */}
        <div className="min-w-[140px]">
          <Label className="text-xs text-gray-500">종목</Label>
          <Select
            value={row.exercise}
            onValueChange={(v) => onUpdate({ exercise: v, raw_metrics: {} })}
          >
            <SelectTrigger className="h-9 mt-1 bg-white" data-testid={`select-exercise-${row._key}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {catConfig.exercises.map((e) => (
                <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Dynamic columns */}
        {cols.map((col) => (
          <div key={col} className="min-w-[100px] flex-1">
            <Label className="text-xs text-gray-500">{columnLabel(category, col)}</Label>
            <Input
              type="number"
              step="any"
              value={row.raw_metrics?.[col] ?? ''}
              onChange={(e) => handleMetricChange(col, e.target.value)}
              className="h-9 mt-1 bg-white"
              data-testid={`input-${row._key}-${col}`}
            />
          </div>
        ))}

        {/* Score badge */}
        <div className="min-w-[90px] text-center">
          <div className="text-xs text-gray-500">점수</div>
          <div className="h-9 mt-1 flex items-center justify-center">
            {computing ? (
              <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
            ) : row.score !== null && row.score !== undefined ? (
              <span className="px-2.5 py-1 rounded-full bg-orange-100 text-orange-700 font-semibold text-sm" data-testid={`score-${row._key}`}>
                {row.score}
              </span>
            ) : (
              <span className="text-gray-300 text-sm">-</span>
            )}
          </div>
        </div>

        {/* Remove */}
        <button
          type="button"
          onClick={onRemove}
          className="h-9 w-9 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
          aria-label="행 삭제"
          data-testid={`btn-remove-row-${row._key}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
